import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';

export const JOB_ROLE_TRANSLATABLE_FIELDS = ['name', 'description', 'tasksDescription'] as const;
export type JobRoleTranslatableField = (typeof JOB_ROLE_TRANSLATABLE_FIELDS)[number];
export type JobRoleTranslations = TranslatableText<JobRoleTranslatableField>;

export const jobRoleQuerySchema = type({
  'name?': 'string',
});

export type JobRoleQuery = typeof jobRoleQuerySchema.t;

export interface JobRole {
  id: string;
  name: string;
}

export const createJobRoleSchema = type({
  name: type('string').configure({
    description: 'Display name of the job role.',
    examples: ['Care Assistant', 'Nurse'],
  }),
  'description?': type('string | null').configure({
    description: 'Short description — one line of what the profile stands for. `null` clears the value.',
  }),
  'tasksDescription?': type('string | null').configure({
    description: "Long-form description of the profile's tasks and areas of responsibility. `null` clears the value.",
  }),
  'skillIds?': type('string[]').configure({
    description: 'Required skills (skills-catalogue ids) — the FULL member set; unknown ids are rejected (400).',
  }),
  'translations?': translatableTextSchema(JOB_ROLE_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name`, `description` and `tasksDescription`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload for creating a new job role.' });

export type CreateJobRole = typeof createJobRoleSchema.t;

export const updateJobRoleSchema = type({
  'name?': type('string').configure({
    description: 'Updated display name.',
    examples: ['Care Assistant'],
  }),
  'description?': type('string | null').configure({
    description: 'Updated short description. `null` clears the value.',
  }),
  'tasksDescription?': type('string | null').configure({
    description: 'Updated tasks/areas description. `null` clears the value.',
  }),
  'skillIds?': type('string[]').configure({
    description: 'FULL replacement set of required skill ids; unknown ids are rejected (400).',
  }),
  'translations?': translatableTextSchema(JOB_ROLE_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({ description: 'Partial update of a job role — fields omitted from the body remain unchanged.' });

export type UpdateJobRole = typeof updateJobRoleSchema.t;

/**
 * Job role response type returned by the API.
 */
export interface JobRoleResponse {
  id: string;
  name: string;
  /** Short description — one line of what the profile stands for. */
  description: string | null;
  /** Long-form description of the profile's tasks and areas of responsibility (PO decision 2026-07-09). */
  tasksDescription: string | null;
  /** Required skills of the profile (skills-catalogue ids). */
  skillIds: string[];
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
